import { useContext } from "react";
import { useTheme } from "styled-components";
import { round } from "@shared/utils/numbers";
import {
  addFeeToPrice,
  convertPriceToMonthlyPrice,
  getInitialPrice,
  getInitialTotalPrice,
  getPlanPrice,
} from "@shared/features/settings/subscription/utils";
import { BasePlanDetails } from "@shared/types";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context.tsx";
import { useGetPrices } from "features/settings/Subscription/useGetPrices.ts";

export const usePromoPrices = () => {
  const theme = useTheme();
  const {
    plan,
    duration,
    promoCodePrices,
    showUpgradePrices,
    isCryptoPayment,
  } = useContext(BuySubscriptionContext)!;

  const { planPrice, extraFee: planExtraFee, totalPrice } = useGetPrices();
  const { extraFee: promoCodeExtraFee, totalPrice: promoCodeTotalPrice } =
    useGetPrices(true);

  const extraFee = promoCodeExtraFee || planExtraFee;
  const newPlan = showUpgradePrices ? plan?.upgradePlan : plan?.[duration];

  const getPlanPrices = (promoCodePlanPrice?: number) => {
    const price = promoCodePlanPrice || getPlanPrice(newPlan);
    const monthlyPrice = newPlan
      ? convertPriceToMonthlyPrice(price, newPlan.subscription_days)
      : 0;
    return [price, monthlyPrice];
  };

  const [originalPlanPrice, originalPlanPricePerMonth] = getPlanPrices();
  const initialUpgradePrice = round(
    getInitialPrice(plan, duration) - originalPlanPrice + (planPrice || 0),
    2
  );
  const initialUpgradeTotalPrice = addFeeToPrice(
    initialUpgradePrice,
    isCryptoPayment
  );
  const initialTotalPrice = showUpgradePrices
    ? initialUpgradeTotalPrice
    : getInitialTotalPrice(plan, duration, isCryptoPayment);

  const isPromoCodeApplied = promoCodePrices?.[duration]?.code === 200;
  const promoCodeMessage = promoCodePrices?.[duration]?.message;
  const discount = isPromoCodeApplied
    ? round(
        ((initialTotalPrice - promoCodeTotalPrice) / initialTotalPrice) * 100,
        0
      )
    : 0;

  const getPromoCodeDiscountMessage = () => {
    if (isPromoCodeApplied) {
      return `${discount}% discount applied`;
    }
  };

  const getResultMessageColor = () => {
    if (isPromoCodeApplied) {
      if (promoCodeMessage?.length) return theme.colors.gold;
      else return theme.colors.green;
    }
    return theme.colors.red;
  };

  return {
    discount,
    extraFee,
    initialTotalPrice,
    originalPlanPricePerMonth,
    totalPrice,
    promoCodeTotalPrice,
    newPlan,
    originalPlanPrice,
    initialUpgradePrice,
    getPlanPrices,
    planPrice,
    getPromoCodeDiscountMessage,
    getResultMessageColor,
    promoCodeMessage,
    isPromoCodeApplied,
  };
};
